import { ModelRunError } from '@/api/errors';
import { I18N_KEYS } from '@/language/keys';
import type { TFunction } from 'i18next';

export enum CompositorAiErrorCode {
  WorkflowRequired = 'WORKFLOW_REQUIRED',
  SourceUnavailable = 'SOURCE_UNAVAILABLE',
  SourceNotUploaded = 'SOURCE_NOT_UPLOADED',
  SourceChanged = 'SOURCE_CHANGED',
  AlreadyRunning = 'ALREADY_RUNNING',
  EmptyResult = 'EMPTY_RESULT',
  InvalidResult = 'INVALID_RESULT',
  ApplyFailed = 'APPLY_FAILED',
  CancellationFailed = 'CANCELLATION_FAILED',
}

/** The UI uses the translation key; the optional message provides details for diagnostic logs. */
export class CompositorAiError extends Error {
  constructor(
    public readonly key: CompositorAiErrorCode,
    message: string = key,
  ) {
    super(message);
    this.name = 'CompositorAiError';
  }
}

/** Formats compositor AI errors for workflow-level failure notifications. */
export function formatCompositorAiError(error: unknown, t: TFunction): string {
  if (error instanceof CompositorAiError) return t(I18N_KEYS.COMPOSITOR.AI_ACTIONS.ERRORS[error.key]);
  if (error instanceof ModelRunError) {
    const keys = I18N_KEYS.COMPOSITOR.AI_ACTIONS.MODEL_ERRORS;
    const modelKeys = {
      failed: keys.FAILED,
      canceled: keys.CANCELED,
      timeout: keys.TIMEOUT,
      missing_prediction: keys.MISSING_PREDICTION,
    };
    let message = t(modelKeys[error.kind]);
    if (error.kind === 'failed' && error.providerMessage)
      message = t(keys.PROVIDER, { message: error.providerMessage });
    if (error.predictionId) message = t(keys.PREDICTION, { message, predictionId: error.predictionId });
    return message;
  }
  return t(I18N_KEYS.COMPOSITOR.AI_ACTIONS.ERROR);
}
